import {
  Badge,
  Button,
  Calendar,
  CalendarProps,
  Col,
  Input,
  Popover,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd"
import { useTranslation } from "react-i18next"
import { ReactComponent as TableIcon } from "assets/icons/table.svg"
import { ReactComponent as CalendarIcon } from "assets/icons/calendar.svg"
import { ReactComponent as ShowIcon } from "assets/icons/show.svg"
import { ReactComponent as CalendarDownloadIcon } from "assets/icons/calendar-download.svg"
import { ReactComponent as AbsentIcon } from "assets/icons/absent.svg"
import { ReactComponent as PresentIcon } from "assets/icons/circle.svg"

import styles from "./styles.module.scss"
import { useEffect, useMemo, useState } from "react"
import CircleButton from "@atoms/icon-button"
import { formatDay } from "utils/format-datetime"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMemberId } from "pages/member"
import {
  useCalendarSubscriptions,
  useExportMemberEventsIcal,
  useMemberEvents,
  useToggleAbsence,
} from "./queries"
import { useEventsTab } from ".."
import dayjs, { Dayjs } from "dayjs"
import Loader from "components/loader"
import PlannedAbsenceModal from "./plannedAbsenceModal"
import { usePopup } from "utils/use-popup"

const { Text, Title } = Typography
const { Search } = Input

const ACTIVITY_PARAM = "q"
const VIEW_PARAM = "view"

const viewType = ["table", "calendar"] as const
type ViewType = typeof viewType
const isViewType = (view: any): view is ViewType => viewType.includes(view)

const Subscriptions = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const activeTab = useEventsTab()
  const memberId = useMemberId()
  const plannedAbsenceModal = usePopup<string>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [calendarValue, setCalendarValue] =
    useState<CalendarProps<Dayjs>["value"]>(dayjs)

  const memberEvents = useMemberEvents(memberId!, undefined, {
    enabled: searchParams.get("view") !== "calendar",
  })

  const calendarSubscriptions = useCalendarSubscriptions(
    memberId!,
    {
      view: "month",
      date: calendarValue!.format("YYYY-MM-DD"),
    },
    {
      enabled: searchParams.get("view") === "calendar",
    }
  )

  const filteredEvents = useMemo(() => {
    if (searchParams.get(ACTIVITY_PARAM)) {
      return memberEvents.data
        ?.filter(
          (element) =>
            element.name
              .toLowerCase()
              .startsWith(searchParams.get(ACTIVITY_PARAM)!.toLowerCase()) && element
        )
        .sort((a, b) => a.name.localeCompare(b.name))
    }
    return memberEvents.data
  }, [memberEvents.data, searchParams])

  const dateCellRender = (value: any) => {
    return (
      <ul className="events">
        {calendarSubscriptions.data?.map((item) => {
          if (item.start_date === value.format("YYYY-MM-DD")) {
            return (
              <Popover
                key={item.id}
                content={() => (
                  <div>
                    <Title level={5}>{item.name}</Title>
                    <Text>
                      {item.start_hour} a {item.end_hour}
                    </Text>
                  </div>
                )}
              >
                <li
                  onClick={() => navigate(`../show/${item.id}`)}
                  className={styles.calendarItemContainer}
                >
                  <div className={styles.calendarItem}>
                    <span />
                    <p>{item.name}</p>
                  </div>
                </li>
              </Popover>
            )
          } else {
            return null
          }
        })}
      </ul>
    )
  }

  const memberEventsIcal = useExportMemberEventsIcal(memberId!)

  const { mutate: toggleAbsence } = useToggleAbsence(memberId!)

  const onClickDownload = () => {
    memberEventsIcal.mutate()
  }

  useEffect(() => {
    // added tab because useEffect is running event if the tab is not selected
    if (
      !isViewType(searchParams.get(VIEW_PARAM)) &&
      activeTab === "subscriptions"
    ) {
      searchParams.set(VIEW_PARAM, "table")
      setSearchParams(searchParams, { replace: true })
    }
  })

  return (
    <>
      <Row style={{ marginBottom: 15 }} justify={"space-between"}>
        <Col>
          {searchParams.get(VIEW_PARAM) === "table" && (
            <Search
              value={searchParams.get(ACTIVITY_PARAM) || ""}
              onChange={(e) => {
                const newValue = e.target.value
                newValue !== ""
                  ? searchParams.set(ACTIVITY_PARAM, newValue)
                  : searchParams.delete(ACTIVITY_PARAM)
                setSearchParams(searchParams, { replace: true })
              }}
              placeholder={t("global.search")}
              style={{ width: "100%" }}
            />
          )}
        </Col>
        <Col>
          <Space>
            <Tooltip title={t("events.download_ical")}>
              <CircleButton
                onClick={onClickDownload}
                loading={memberEventsIcal.isLoading}
                icon={<CalendarDownloadIcon />}
              />
            </Tooltip>
            <Tooltip
              title={t(
                searchParams.get(VIEW_PARAM) === "table"
                  ? "global.calendar"
                  : "global.table"
              )}
            >
              <CircleButton
                onClick={() => {
                  searchParams.set(
                    VIEW_PARAM,
                    searchParams.get(VIEW_PARAM) === "table"
                      ? "calendar"
                      : "table"
                  )
                  setSearchParams(searchParams, { replace: true })
                }}
                icon={
                  searchParams.get(VIEW_PARAM) === "table" ? (
                    <CalendarIcon />
                  ) : (
                    <TableIcon />
                  )
                }
              />
            </Tooltip>
          </Space>
        </Col>
      </Row>
      {searchParams.get(VIEW_PARAM) === "calendar" ? (
        <div className={styles.calendarMonthMode}>
          <Loader
            spinProps={{
              spinning:
                calendarSubscriptions.isLoading ||
                calendarSubscriptions.isPreviousData,
            }}
          >
            <Calendar
              cellRender={dateCellRender}
              mode="month"
              value={calendarValue}
              onChange={setCalendarValue}
            />
          </Loader>
        </div>
      ) : (
        <Table
          loading={memberEvents.isLoading}
          pagination={false}
          tableLayout="fixed"
          className={styles.table}
          rowKey={"id"}
          columns={[
            {
              title: t<string>("events.activities"),
              dataIndex: "name",
              key: "activities",
              ellipsis: true,
              render: (value) => (
                <Tooltip placement="bottomLeft" title={value}>
                  {value}
                </Tooltip>
              ),
            },
            {
              title: t<string>("events.title"),
              dataIndex: "start_date",
              key: "events",
              ellipsis: true,
              sorter: (a, b) => {
                const dateA = dayjs(
                  a.start_date + " " + a.start_hour,
                  "YYYY-MM-DD HH:mm"
                )
                const dateB = dayjs(
                  b.start_date + " " + b.start_hour,
                  "YYYY-MM-DD HH:mm"
                )
                return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0
              },
              render: (text, record) => {
                const value = `${formatDay(record.start_date)}, ${
                  record.start_hour
                }h - ${record.end_hour}h`
                return (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                )
              },
            },
            {
              title: t<string>("global.status"),
              dataIndex: "displayed_status",
              key: "status",
              ellipsis: true,
              render: (value) => (
                <Tooltip placement="bottomLeft" title={value}>
                  <Badge>{value}</Badge>
                </Tooltip>
              ),
            },
            {
              title: t<string>("global.actions"),
              key: "action",
              width: 100,
              render: (_, record) => (
                <>
                  <Tooltip placement="bottom" title={t("global.show")}>
                    <Button
                      type="text"
                      shape="circle"
                      onClick={() => navigate(`../show/${record.id}`)}
                      icon={<ShowIcon />}
                    />
                  </Tooltip>
                  <Tooltip
                    placement="bottom"
                    mouseLeaveDelay={0}
                    title={
                      record.planned_absent
                        ? t("events.absent")
                        : t("events.present")
                    }
                  >
                    <Button
                      type="text"
                      shape="circle"
                      disabled={!["draft", "approved"].includes(record.status!)}
                      onClick={() => {
                        if (record.planned_absent)
                          toggleAbsence({ eventId: record.id })
                        else plannedAbsenceModal.open(record.id)
                      }}
                      icon={
                        record.planned_absent ? <AbsentIcon /> : <PresentIcon />
                      }
                    />
                  </Tooltip>
                  {/* <Tooltip
                              placement="bottom"
                              title={t("events.unsubscribe")}
                            >
                              <Button
                                type="text"
                                shape="circle"
                                disabled={
                                  !["draft", "approved"].includes(
                                    record.status!
                                  )
                                }
                                onClick={() => unsubscribeModal.open(record)}
                                icon={<LeaveIcon />}
                              />
                            </Tooltip> */}
                </>
              ),
            },
          ]}
          dataSource={filteredEvents}
        />
      )}
      <PlannedAbsenceModal
        {...plannedAbsenceModal}
        open={plannedAbsenceModal.isOpen}
      />
    </>
  )
}

export default Subscriptions
